import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Stack,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Description as FileIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { EasyCheckButton } from '../../common/Controls';
import { beneficiaryImportService } from '../../../services/beneficiaries/beneficiaryImportService';
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadArea = styled(Box)(({ theme, isDragOver }) => ({
  border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.grey[300]}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: isDragOver ? theme.palette.primary.light + '10' : theme.palette.grey[50],
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '10',
  }
}));

const BeneficiaryImportModal = ({ open, onClose, onImportComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0, status: '' });
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleClose = () => {
    if (!isProcessing) {
      setActiveStep(0);
      setSelectedFile(null);
      setImportResults(null);
      setError(null);
      setProgress({ current: 0, total: 0, percentage: 0, status: '' });
      onClose();
    }
  };

  const handleDownloadTemplate = () => {
    try {
      beneficiaryImportService.downloadTemplate();
    } catch (error) {
      setError('Failed to download template: ' + error.message);
    }
  };

  const handleFileSelect = (file) => {
    if (file && file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please select a valid Excel file (.xlsx)');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);
    setActiveStep(1);
    
    try {
      const results = await beneficiaryImportService.importBeneficiaries(selectedFile, (progressData) => {
        setProgress(progressData);
      });

      setImportResults(results);
      setActiveStep(2);
      
      if (onImportComplete) {
        onImportComplete(results);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadResults = () => {
    if (importResults) {
      beneficiaryImportService.downloadResultFile(importResults.results, selectedFile.name);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS': return 'success';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS': return <SuccessIcon fontSize="small" />;
      case 'FAILED': return <ErrorIcon fontSize="small" />;
      default: return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Import Beneficiaries</Typography>
        <IconButton onClick={handleClose} disabled={isProcessing}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 3 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>Upload File</StepLabel>
            <StepContent>
              <Stack spacing={2} sx={{ mt: 4 }}>
                <Card sx={{ backgroundColor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon color="primary" />
                      Before you start
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <DownloadIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Download and use our Excel template to ensure correct formatting" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <SuccessIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Required fields: Beneficiary Name, Company ID" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <InfoIcon color="info" />
                        </ListItemIcon>
                        <ListItemText primary="Maximum 1000 records per import" />
                      </ListItem>
                    </List>
                    <EasyCheckButton
                      buttonType="action"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadTemplate}
                      sx={{ mt: 2 }}
                    >
                      Download Template
                    </EasyCheckButton>
                  </CardContent>
                </Card>

                <UploadArea
                  isDragOver={isDragOver}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Stack spacing={2} alignItems="center">
                    <UploadIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.7 }} />
                    <Typography variant="h6" color="textSecondary">
                      {selectedFile ? selectedFile.name : 'Drop your Excel file here or click to browse'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Supported format: .xlsx
                    </Typography>
                    <EasyCheckButton 
                      buttonType="action"
                      variant="contained" 
                      component="label"
                    >
                      Select File
                      <VisuallyHiddenInput
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx"
                        onChange={handleFileChange}
                      />
                    </EasyCheckButton>
                  </Stack>
                </UploadArea>

                {selectedFile && (
                  <Alert severity="success" icon={<FileIcon />}>
                    Selected file: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </Alert>
                )}

                {error && (
                  <Alert severity="error">{error}</Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <EasyCheckButton 
                    buttonType="form"
                    variant="outlined"
                    onClick={handleClose}
                  >
                    Cancel
                  </EasyCheckButton>
                  <EasyCheckButton
                    buttonType="action"
                    variant="contained"
                    onClick={handleImport}
                    disabled={!selectedFile}
                    loading={isProcessing}
                  >
                    Start Import
                  </EasyCheckButton>
                </Box>
              </Stack>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Import Progress</StepLabel>
            <StepContent>
              <Stack spacing={2} sx={{ mt: 4 }}>
                <Box>
                  <Typography variant="body1" gutterBottom>
                    {progress.status || 'Processing...'}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={progress.percentage} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {progress.current} of {progress.total} records processed ({progress.percentage}%)
                  </Typography>
                </Box>

                {error && (
                  <Alert severity="error">{error}</Alert>
                )}
              </Stack>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Import Results</StepLabel>
            <StepContent>
              {importResults && (
                <Stack spacing={2} sx={{ mt: 4 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Import Summary</Typography>
                      <Stack direction="row" spacing={2}>
                        <Chip 
                          label={`Total: ${importResults.summary?.total || 0}`} 
                          color="default"
                          variant="outlined"
                        />
                        <Chip 
                          label={`Success: ${importResults.summary?.successful || 0}`} 
                          color="success"
                          variant="outlined"
                        />
                        <Chip 
                          label={`Failed: ${importResults.summary?.failed || 0}`} 
                          color="error"
                          variant="outlined"
                        />
                      </Stack>
                    </CardContent>
                  </Card>

                  {importResults.results && importResults.results.length > 0 && (
                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Row</TableCell>
                            <TableCell>Beneficiary Name</TableCell>
                            <TableCell>Company ID</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Message</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {importResults.results.map((result, index) => (
                            <TableRow key={index}>
                              <TableCell>{result.rowNumber}</TableCell>
                              <TableCell>{result.data['Beneficiary Name']}</TableCell>
                              <TableCell>{result.data['Company ID']}</TableCell>
                              <TableCell>
                                <Chip
                                  label={result.status}
                                  color={getStatusColor(result.status)}
                                  size="small"
                                  icon={getStatusIcon(result.status)}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                  {result.message}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <EasyCheckButton
                      buttonType="action"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadResults}
                    >
                      Download Results
                    </EasyCheckButton>
                    <EasyCheckButton 
                      buttonType="form"
                      variant="contained" 
                      onClick={handleClose}
                    >
                      Close
                    </EasyCheckButton>
                  </Box>
                </Stack>
              )}
            </StepContent>
          </Step>
        </Stepper>
      </DialogContent>
    </Dialog>
  );
};

export default BeneficiaryImportModal; 